import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import TimerIcon from '@mui/icons-material/Timer';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ScatterChart,
  Scatter,
  BarChart,
  Bar,
} from "recharts";
import Grid from "@mui/material/Grid2";
import pb from "../pocketBase";
import { CTM_Colors, IFeedbackData, IRunData } from "../Helper";
import { toast } from "react-toastify";
import Loading from "../Components/Loading";

interface RunWithDetails extends IRunData {
  id: string;
  created: string;
  feedback?: IFeedbackData;
  times?: Array<{ Time: string; Cones: number }>;
  sessionName?: string;
}

// Shared styling helpers

function chartPaperSx(borderColor: string) {
  return {
    p: 3,
    borderRadius: 2,
    borderTop: `4px solid ${borderColor}`,
    transition: 'box-shadow 0.3s ease',
    '&:hover': { boxShadow: 8 },
  };
}

function tooltipContentStyle(borderColor: string) {
  return {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    border: `2px solid ${borderColor}`,
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  };
}

const chartHeadingSx = { mb: 2, fontWeight: 600, color: CTM_Colors.ctm_green_dark };

// Calculate the best lap time for a run (raw time + 2s penalty per cone)
function getBestLapTime(times: Array<{ Time: string; Cones: number }>): number {
  return Math.min(...times.map((t) => parseFloat(t.Time) + t.Cones * 2));
}

// Feedback metric labels mapped to their IFeedbackData keys
const feedbackMetrics: Array<{ metric: string; key: keyof IFeedbackData }> = [
  { metric: "Grip", key: "grip" },
  { metric: "Control Entry", key: "control_corner_entry" },
  { metric: "Control Apex", key: "control_corner_apex" },
  { metric: "Control Exit", key: "control_corner_exit" },
  { metric: "Stability Entry", key: "stability_entry" },
  { metric: "Stability Apex", key: "stability_apex" },
  { metric: "Stability Exit", key: "stability_exit" },
  { metric: "Braking Stability", key: "braking_stability" },
  { metric: "Throttle Stability", key: "throttle_stability" },
];

// KPI Card component (defined outside DashboardPage to avoid re-creation on each render)
interface KpiCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  gradient: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ title, value, icon, gradient }) => (
  <Card
    sx={{
      height: '100%',
      background: gradient,
      color: 'white',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      '&:hover': {
        transform: 'scale(1.03)',
        boxShadow: 8,
      }
    }}
    elevation={4}
  >
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
          {title}
        </Typography>
        <Box sx={{ opacity: 0.9 }}>
          {icon}
        </Box>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

function formatTimeValue(time: number): string {
  return time > 0 ? `${time.toFixed(3)}s` : 'N/A';
}

const DashboardPage: React.FC = () => {
  const [runs, setRuns] = useState<RunWithDetails[]>([]);
  const [sessions, setSessions] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedSession, setSelectedSession] = useState<string>("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [selectedSession]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const sessionsList = await pb.collection("TestingSession").getFullList();
      setSessions(sessionsList.map(s => ({ id: s.id, name: s.name })));

      const options: any = {
        expand: "sessionId",
        sort: "created",
      };

      if (selectedSession !== "all") {
        options.filter = `sessionId = "${selectedSession}"`;
      } else if (sessionsList.length > 0) {
        options.filter = sessionsList.map(s => `sessionId = "${s.id}"`).join(" || ");
      } else {
        setRuns([]);
        return;
      }

      const runsList = await pb.collection("Run").getFullList<any>(options);

      const runsWithDetails: RunWithDetails[] = [];
      for (const run of runsList) {
        const times = await pb
          .collection("Time")
          .getFullList({ filter: `RunId = "${run.id}"`, sort: "Index" })
          .catch(() => []);

        const feedback = await pb
          .collection("DriverFeedback")
          .getFirstListItem(`run = "${run.id}"`)
          .catch(() => null);

        runsWithDetails.push({
          ...run,
          feedback,
          times,
          sessionName: run.expand?.sessionId?.name || "Unknown",
        });
      }

      setRuns(runsWithDetails);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleSessionChange = (event: SelectChangeEvent<string>) => {
    setSelectedSession(event.target.value);
  };

  // Filter runs that have valid times once, reused by multiple data computations
  const runsWithTimes = runs.filter((run) => run.times && run.times.length > 0);

  const lapTimeProgressionData = runsWithTimes.map((run, index) => {
    const bestLap = getBestLapTime(run.times!);
    return {
      runNumber: index + 1,
      bestLap: parseFloat(bestLap.toFixed(3)),
      driver: run.driver || "Unknown",
      event: run.event || "Unknown",
      date: new Date(run.created).toLocaleDateString(),
    };
  });

  // Build radar data from the last run's feedback
  const lastFeedback = runs.length > 0 ? runs[runs.length - 1]?.feedback : null;
  const feedbackRadarData = lastFeedback
    ? feedbackMetrics.map(({ metric, key }) => ({
        metric,
        value: lastFeedback[key] as number,
      }))
    : [];

  const tirePerformanceData = runsWithTimes
    .filter((run) => run.tire_compound)
    .reduce((acc: any[], run) => {
      const bestLap = getBestLapTime(run.times!);
      const existing = acc.find((item) => item.compound === run.tire_compound);
      if (existing) {
        existing.count++;
        existing.avgTime = (existing.avgTime * (existing.count - 1) + bestLap) / existing.count;
        existing.bestTime = Math.min(existing.bestTime, bestLap);
      } else {
        acc.push({
          compound: run.tire_compound,
          avgTime: bestLap,
          bestTime: bestLap,
          count: 1,
        });
      }
      return acc;
    }, []);

  const weatherData = runsWithTimes
    .filter((run) => run.track_temperature !== undefined)
    .map((run) => {
      const bestLap = getBestLapTime(run.times!);
      return {
        trackTemp: run.track_temperature,
        lapTime: parseFloat(bestLap.toFixed(3)),
        humidity: run.ambient_humidity,
      };
    });

  if (loading) {
    return <Loading message="Loading dashboard data..." />;
  }

  if (runs.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 4, color: CTM_Colors.ctm_green }}>
          Performance Dashboard
        </Typography>
        <FormControl sx={{ minWidth: 300, mb: 4 }}>
          <InputLabel>Session Filter</InputLabel>
          <Select value={selectedSession} onChange={handleSessionChange} label="Session Filter">
            <MenuItem value="all">All Sessions</MenuItem>
            {sessions.map((session) => (
              <MenuItem key={session.id} value={session.id}>{session.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          minHeight: '40vh', gap: 2, color: 'text.secondary',
        }}>
          <DirectionsCarIcon sx={{ fontSize: 64, opacity: 0.3 }} />
          <Typography variant="h6">No data yet</Typography>
          <Typography variant="body2">
            {selectedSession === 'all'
              ? 'Add some runs to a session to see performance charts here.'
              : 'This session has no runs yet.'}
          </Typography>
        </Box>
      </Container>
    );
  }

  // KPI metrics
  const allLapTimes = runs.flatMap(run =>
    run.times?.map(t => parseFloat(t.Time)).filter(t => !isNaN(t) && t > 0) || []
  );
  const bestLapTime = allLapTimes.length > 0 ? Math.min(...allLapTimes) : 0;
  const avgLapTime = allLapTimes.length > 0
    ? allLapTimes.reduce((a, b) => a + b, 0) / allLapTimes.length
    : 0;
  const uniqueDrivers = new Set(runs.map(r => r.driver)).size;

  const kpiCards = [
    {
      title: "Best Lap Time",
      value: formatTimeValue(bestLapTime),
      icon: <TimerIcon sx={{ fontSize: 40 }} />,
      gradient: `linear-gradient(135deg, ${CTM_Colors.ctm_green} 0%, ${CTM_Colors.ctm_green_dark} 100%)`,
    },
    {
      title: "Average Lap Time",
      value: formatTimeValue(avgLapTime),
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      gradient: `linear-gradient(135deg, ${CTM_Colors.chart_secondary} 0%, ${CTM_Colors.ctm_green} 100%)`,
    },
    {
      title: "Total Runs",
      value: runs.length.toString(),
      icon: <DirectionsCarIcon sx={{ fontSize: 40 }} />,
      gradient: `linear-gradient(135deg, ${CTM_Colors.chart_tertiary} 0%, #43a047 100%)`,
    },
    {
      title: "Drivers",
      value: uniqueDrivers.toString(),
      icon: <PersonIcon sx={{ fontSize: 40 }} />,
      gradient: `linear-gradient(135deg, ${CTM_Colors.success} 0%, #388e3c 100%)`,
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 2, color: CTM_Colors.ctm_green }}>
          Performance Dashboard
        </Typography>
        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel>Session Filter</InputLabel>
          <Select value={selectedSession} onChange={handleSessionChange} label="Session Filter">
            <MenuItem value="all">All Sessions</MenuItem>
            {sessions.map((session) => (
              <MenuItem key={session.id} value={session.id}>
                {session.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* KPI Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {kpiCards.map((card) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={card.title}>
            <KpiCard
              title={card.title}
              value={card.value}
              icon={card.icon}
              gradient={card.gradient}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Lap Time Progression */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper elevation={4} sx={chartPaperSx(CTM_Colors.ctm_green)}>
            <Typography variant="h6" sx={chartHeadingSx}>
              Lap Time Progression
            </Typography>
            {lapTimeProgressionData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={lapTimeProgressionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis
                    dataKey="runNumber"
                    label={{ value: "Run Number", position: "insideBottom", offset: -5 }}
                  />
                  <YAxis
                    label={{ value: "Best Lap Time (s)", angle: -90, position: "insideLeft" }}
                  />
                  <Tooltip
                    formatter={(value: any) => [`${value}s`, "Best Lap"]}
                    labelFormatter={(label) => `Run ${label}`}
                    contentStyle={tooltipContentStyle(CTM_Colors.ctm_green)}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="bestLap"
                    stroke={CTM_Colors.ctm_green}
                    strokeWidth={3}
                    dot={{ r: 5, fill: CTM_Colors.ctm_green, strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 7, fill: CTM_Colors.ctm_green_dark }}
                    name="Best Lap Time"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary">No lap time data available</Typography>
            )}
          </Paper>
        </Grid>

        {/* Driver Feedback Radar */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper elevation={4} sx={chartPaperSx(CTM_Colors.chart_secondary)}>
            <Typography variant="h6" sx={chartHeadingSx}>
              Latest Driver Feedback
            </Typography>
            {feedbackRadarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={feedbackRadarData}>
                  <PolarGrid stroke="#e0e0e0" />
                  <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 10]} />
                  <Radar
                    name="Feedback"
                    dataKey="value"
                    stroke={CTM_Colors.chart_secondary}
                    fill={CTM_Colors.chart_secondary}
                    fillOpacity={0.7}
                    strokeWidth={2}
                  />
                  <Tooltip contentStyle={tooltipContentStyle(CTM_Colors.chart_secondary)} />
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary">No feedback data available</Typography>
            )}
          </Paper>
        </Grid>

        {/* Tire Compound Performance */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={4} sx={chartPaperSx(CTM_Colors.chart_tertiary)}>
            <Typography variant="h6" sx={chartHeadingSx}>
              Tire Compound Performance
            </Typography>
            {tirePerformanceData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={tirePerformanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="compound" />
                  <YAxis label={{ value: "Time (s)", angle: -90, position: "insideLeft" }} />
                  <Tooltip contentStyle={tooltipContentStyle(CTM_Colors.chart_tertiary)} />
                  <Legend />
                  <Bar
                    dataKey="bestTime"
                    fill={CTM_Colors.chart_tertiary}
                    name="Best Time"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="avgTime"
                    fill={CTM_Colors.chart_secondary}
                    name="Average Time"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary">No tire performance data available</Typography>
            )}
          </Paper>
        </Grid>

        {/* Weather Impact */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={4} sx={chartPaperSx(CTM_Colors.info)}>
            <Typography variant="h6" sx={chartHeadingSx}>
              Track Temperature vs Lap Time
            </Typography>
            {weatherData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis
                    dataKey="trackTemp"
                    name="Track Temp"
                    unit="°C"
                    label={{ value: "Track Temperature (°C)", position: "insideBottom", offset: -5 }}
                  />
                  <YAxis
                    dataKey="lapTime"
                    name="Lap Time"
                    unit="s"
                    label={{ value: "Lap Time (s)", angle: -90, position: "insideLeft" }}
                  />
                  <Tooltip
                    cursor={{ strokeDasharray: "3 3" }}
                    contentStyle={tooltipContentStyle(CTM_Colors.info)}
                  />
                  <Scatter
                    name="Runs"
                    data={weatherData}
                    fill={CTM_Colors.info}
                    shape="circle"
                  />
                </ScatterChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary">No weather data available</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DashboardPage;
